import { BrowserModule, Title } from '@angular/platform-browser';
import { NgModule, ErrorHandler, isDevMode, APP_INITIALIZER } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';


import { AppRoutes } from './app.routes';
import { AppComponent } from './app.component';

import { TopbarComponent } from './components/topbar/topbar.component';
import { MenuComponent } from './components/menu/menu.component';
import { CompanyListComponent } from './components/company/company-list/company-list.component';
import { CompanyDetailComponent } from './components/company/company-detail/company-detail.component';
import { JobListComponent } from './components/job/job-list/job-list.component';
import { PostJobAnalysisComponent } from './components/job/post-job-analysis/post-job-analysis.component';
import { UserListComponent } from './components/user/user-list/user-list.component';
import { UserDetailComponent } from './components/user/user-detail/user-detail.component';
import { DeviceListComponent } from './components/device/device-list/device-list.component';
import { DeviceDetailComponent } from './components/device/device-detail/device-detail.component';
import { DeviceDiagnosticsComponent } from './components/device/device-diagnostics/device-diagnostics.component';
import { OfflineWorkbookComponent } from './components/offlineWorkbook/offlineWorkbook.component';
import { OfflineWorkbookDetailComponent } from './components/offlineWorkbook/offlineWorkbook-detail/offlineWorkbook-detail.component';
import { WorkbookConfigurationComponent } from './components/shared/workbook-configuration/workbook-configuration.component';
import { CalculationsComponent } from './components/shared/calculations/calculations.component';
import { SharedModule } from './shared.module';

import {
  OfflineService,
  ICountyDataService, CountyDataService,
  IDistrictService, DistrictService,
  UserAuthorizationService,
  IWorkbookLogService, WorkbookLogService,
  IFeaturesService, FeaturesService,
  IJobWorkbookConfigurationService, JobWorkbookConfigurationService,
  ICompanyCalculationsService, CompanyCalculationsService,
  IReportService, ReportService,
  INewCompanyRequestService, NewCompanyRequestService,
  ICompanyAssociationRequestService, CompanyAssociationRequestService,
  ICalculatedFieldConfigurationService, CalculatedFieldConfigurationService,
  IJobLinkerService, JobLinkerService
} from './services/services';

import { NameTransform } from './pipes/nameTransform';
import { NameListTransform } from './pipes/nameListTransform';
import { MsalModule, MsalInterceptor, MsalService, MsalInterceptorConfiguration, MsalGuardConfiguration, MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalGuard, MsalBroadcastService, MsalRedirectComponent } from '@azure/msal-angular';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CustomMsalInterceptor } from './interceptors/custom-msal.interceptor';

import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { FieldsetModule } from 'primeng/fieldset';
import { MultiSelectModule } from 'primeng/multiselect';
import { DialogModule } from 'primeng/dialog';
import { InputMaskModule } from 'primeng/inputmask';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PopoverModule } from 'primeng/popover';
import { MessageService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
//import { MonacoEditorModule, NgxMonacoEditorConfig } from 'ngx-monaco-editor';

import { SimulatorModule } from './simulator/simulator.module';

import { CanEditGuard } from './guards/canEdit.guard';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { CTWOTubingODTransform } from './pipes/ct-woTubingODTransform';

import { IWorkbookV2Service } from './services/iWorkbookV2.service';
import { WorkbookV2Service } from './services/workbookV2.service';
import { IOfflineService } from './services/iOffline.service';
import { IOfflineWorkbookService } from './services/iOfflineWorkbook.service';
import { OfflineworkbookService } from './services/offlineworkbook.service';
import { IBootstrapWorkbookForOfflineService } from './services/iBootstrapWorkbookForOffline.service';
import { BootstrapWorkbookForOfflineService } from './services/bootstrapWorkbookForOffline.service';
import { IImportPlugDataService } from './services/iimportPlugData.service';
import { ImportPlugDataService } from './services/importPlugData.service';
import { IOpsViewerMessagesService } from './services/iOpsViewerMessages.service';
import { OpsViewerMessagesService } from './services/opsViewerMessages.service';
import { IApplicationContextService, ApplicationContextService } from './services/services';

import { DeviceConfigurationComponent } from './components/shared/device-configuration/device-configuration.component';
import { AddressComponent } from './components/shared/address/address.component';
import { NewCompanyRequestComponent } from './components/shared/requests/new-company-request/new-company-request.component';
// tslint:disable-next-line:max-line-length
import { CompanyAssociationRequestComponent } from './components/shared/requests/company-association-request/company-association-request.component';
import { RequestComponent } from './components/request/request.component';
import { JobDetailsComponent } from './components/shared/jobs/job-details/job-details.component';
import { JobWellInformationComponent } from './components/shared/jobs/job-well-information/job-well-information.component';
import { JobFluidInformationComponent } from './components/shared/jobs/job-fluid-information/job-fluid-information.component';
import { JobPerformanceSummaryComponent } from './components/shared/jobs/job-performance-summary/job-performance-summary.component';
import { JobFluidSummaryComponent } from './components/shared/jobs/job-fluid-summary/job-fluid-summary.component';
import { VendorJobCommonComponent } from './components/shared/jobs/vendor-job-common/vendor-job-common.component';
import { VendorJobComponent } from './components/job/vendor-job/vendor-job.component';
import { ConsumerJobComponent } from './components/job/consumer-job/consumer-job.component';
import { VendorConsumerJobsComponent } from './components/job/vendor-consumer-jobs/vendor-consumer-jobs.component';
import { UserProfileComponent } from './components/shared/user-profile/user-profile.component';
import { NavigationTopBarComponent } from './components/navigation-top-bar/navigation-top-bar.component';
import { JobsRedirectComponent } from './components/jobs-redirect/jobs-redirect.component';
import { ContactInfoComponent } from './components/shared/contact-info/contact-info.component';
import { JobLinkerComponent } from './components/shared/job-linker/job-linker.component';
import { JobLinkerListComponent } from './components/shared/job-linker/job-linker-list/job-linker-list.component';
import { JobLinkerEditComponent } from './components/shared/job-linker/job-linker-edit/job-linker-edit.component';
import { JobLinkerAdminComponent } from './components/job-linker-admin/job-linker-admin.component';
import { CheckForUpdateService } from './services/checkForUpdateService';
import { TcpDeviceComponent } from './components/shared/tcp-device/tcp-device.component';
import { ICurrentUserService } from './services/currentUserService/icurrentuser.service';
import { CurrentUserService } from './services/currentUserService/currentuser.service';
import { getOpsViewerScopes } from './services/currentUserService/auth-scopes';
import { AppConfig } from './models/AppConfig';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { ConsumerJobClient, UserClient, IUserClient, VendorJobClient, JobTicketClient, DeviceClient, CompanyClient, FilesClient, CompanyWorkbookConfigurationClient, JobIntegrationClient, WorkbookConfigurationClient, ShiftSummaryClient } from './services/nswag/nswagclient';
import { JobCacheService } from './services/job-cache.service';
import { ToastModule } from 'primeng/toast';
import { MessageModule } from 'primeng/message';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { TelemetryService } from './services/telemetry.service';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { TicketListComponent } from './components/ticket-system/ticket-list/ticket-list.component';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { FilePopUpComponent } from './components/files/file-pop-up/file-pop-up.component';
import { IUserManagerService } from './services/iUserManager.service';
import { UserManagerService } from './services/userManager.service';
import { ChipModule } from 'primeng/chip';
import { ApiHelpComponent } from './components/api-help/api-help.component';
import { InputNumberModule } from 'primeng/inputnumber';
import { PanelModule } from 'primeng/panel';
import { TextareaModule } from 'primeng/textarea';
import { FileUploadModule } from 'primeng/fileupload';
import { ChartModule } from 'primeng/chart';
import { HasCompanyGuard } from './guards/hascompany.guard';
import { JobIntegrationsComponent } from './components/job-integrations/job-integrations.component';
import { WorkbookComponent } from './components/workbook/workbook.component';
import { ShiftSummaryListComponent } from './components/shift-summary/shift-summary-list/shift-summary-list.component';
import { providePrimeNG } from 'primeng/config';
import OpsViewerPreset from './theme/opsviewer.preset';
import { firstValueFrom } from 'rxjs';

export function loggerCallback(_logLevel: LogLevel, message: string) {
  try {
    const appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: AppConfig.GLOBAL_CONFIG.applicationInsightsKey,
      },
    });
    appInsights.loadAppInsights();
    appInsights.trackTrace({ message, severityLevel: SeverityLevel.Verbose });
    console.log(message);
  } catch (error) {
    console.warn(error);
  }
}

export function MSALInstanceFactory(): IPublicClientApplication {
  const configJson = localStorage.getItem('config');
  if (!configJson) {
    throw new Error('Missing app config in localStorage.');
  }
  AppConfig.GLOBAL_CONFIG = JSON.parse(configJson) as AppConfig;
    // ICompanyWorkbookConfigurationService, CompanyWorkbookConfigurationService,
  let subtenant = AppConfig.GLOBAL_CONFIG.tenant.split(".")[0];
  return new PublicClientApplication({
    auth: {
      // clientId: '6226576d-37e9-49eb-b201-ec1eeb0029b6', // Prod enviroment. Uncomment to use. 
      clientId: AppConfig.GLOBAL_CONFIG.clientID, // PPE testing environment
      // authority: 'https://login.microsoftonline.com/common', // Prod environment. Uncomment to use.
      authority: `https://${subtenant}.b2clogin.com/${subtenant}.onmicrosoft.com/b2c_1_newsignupsigin`, // PPE testing environment.
      knownAuthorities: [`${subtenant}.b2clogin.com`],
      redirectUri: '/',
      postLogoutRedirectUri: '/'
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false
      }
    }
  });
}
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string> | null>();
  // protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']); // Prod environment. Uncomment to use.
  //protectedResourceMap.set('https://graph.microsoft-ppe.com/v1.0/me', ['user.read']);
  //protectedResourceMap.set('/api/*', ['https://opsviewer.onmicrosoft.com/web/read']); //["openid", "profile"]
  protectedResourceMap.set('/api/features', null);
  protectedResourceMap.set('/api/*', getOpsViewerScopes()); //, "read","offline_access"["openid", "profile"]

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    authRequest: (msalService, _httpReq, originalAuthRequest) => {

      return {
        ...originalAuthRequest,
        loginHint: msalService.instance.getActiveAccount()?.username
      };
    }
  }
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { interactionType: InteractionType.Redirect };
}

export function initializeMsalFactory(msalService: MsalService): () => Promise<void> {
  return () => firstValueFrom(msalService.initialize());
}

@NgModule({ declarations: [
        AppComponent,
        TopbarComponent,
        MenuComponent,
        CompanyListComponent,
        CompanyDetailComponent,
        JobListComponent,
        PostJobAnalysisComponent,
        UserListComponent,
        UserDetailComponent,
        DeviceListComponent,
  DeviceDetailComponent,
  DeviceDiagnosticsComponent,
        OfflineWorkbookComponent,
        OfflineWorkbookDetailComponent,
        NameTransform,
        NameListTransform,
        CTWOTubingODTransform,
        WorkbookConfigurationComponent,
        CalculationsComponent,
        DeviceConfigurationComponent,
        AddressComponent,
        NewCompanyRequestComponent,
        CompanyAssociationRequestComponent,
        RequestComponent,
        JobDetailsComponent,
        JobFluidInformationComponent,
        JobPerformanceSummaryComponent,
        JobFluidSummaryComponent,
        VendorJobCommonComponent,
        VendorJobComponent,
        ConsumerJobComponent,
        VendorConsumerJobsComponent,
        UserProfileComponent,
        NavigationTopBarComponent,
        JobsRedirectComponent,
        ContactInfoComponent,
        JobLinkerComponent,
        JobLinkerListComponent,
        JobLinkerEditComponent,
        JobLinkerAdminComponent,
        TcpDeviceComponent,
        FilePopUpComponent,
        ApiHelpComponent,
        JobIntegrationsComponent,
        WorkbookComponent,
        ShiftSummaryListComponent
    ],
    bootstrap: [AppComponent, MsalRedirectComponent], imports: [
      
      MsalModule,
      ChipModule,
        AppRoutes,
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        DatePickerModule,
        SelectModule,
        FieldsetModule,
        MessageModule,
        MultiSelectModule,
        DialogModule,
        DynamicDialogModule,
        InputMaskModule,
        CheckboxModule,
        RadioButtonModule,
        SelectButtonModule,
        SharedModule,
        JobWellInformationComponent,
        ConfirmDialogModule,
        TooltipModule,
        AccordionModule,
        ProgressSpinnerModule,
        PopoverModule,
        InputNumberModule,
        PanelModule,
        TextareaModule,
        FileUploadModule,
        ChartModule,
        CardModule,
        TagModule,
        //ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
        SimulatorModule,
        ToastModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
          enabled: !isDevMode(),
          // Register the ServiceWorker as soon as the application is stable
          // or after 30 seconds (whichever comes first).
          registrationStrategy: 'registerWhenStable:30000'
        })], providers: [
        { provide: CompanyClient, useClass: CompanyClient },
        { provide: ICountyDataService, useClass: CountyDataService },
        { provide: IDistrictService, useClass: DistrictService },
        { provide: IWorkbookLogService, useClass: WorkbookLogService },
  { provide: IFeaturesService, useClass: FeaturesService },
  CompanyWorkbookConfigurationClient,
        { provide: IJobWorkbookConfigurationService, useClass: JobWorkbookConfigurationService },
        { provide: IWorkbookV2Service, useClass: WorkbookV2Service },
        { provide: IOfflineService, useClass: OfflineService },
        { provide: IOfflineWorkbookService, useClass: OfflineworkbookService },
        { provide: IBootstrapWorkbookForOfflineService, useClass: BootstrapWorkbookForOfflineService },
        { provide: ICompanyCalculationsService, useClass: CompanyCalculationsService },
        { provide: IReportService, useClass: ReportService },
        { provide: IImportPlugDataService, useClass: ImportPlugDataService },
        { provide: INewCompanyRequestService, useClass: NewCompanyRequestService },
        { provide: ICompanyAssociationRequestService, useClass: CompanyAssociationRequestService },
        { provide: IOpsViewerMessagesService, useClass: OpsViewerMessagesService },
        { provide: "IConsumerJobClient", useClass: ConsumerJobClient },
        { provide: ICalculatedFieldConfigurationService, useClass: CalculatedFieldConfigurationService },
        { provide: IApplicationContextService, useClass: ApplicationContextService },
        { provide: IJobLinkerService, useClass: JobLinkerService },
        { provide: ICurrentUserService, useClass: CurrentUserService },
        { provide: "IJobTicketClient", useClass: JobTicketClient },
        { provide: UserClient, useClass: UserClient },
        { provide: DynamicDialogConfig, useClass: DynamicDialogConfig },
        { provide: "IVendorJobClient", useClass: VendorJobClient },
        { provide: IUserManagerService, useExisting: UserManagerService },
        FilesClient,
        CheckForUpdateService,
        DeviceClient,
        JobIntegrationClient,
        WorkbookConfigurationClient,
        ShiftSummaryClient,
        CanEditGuard,
        HasCompanyGuard,
        ConfirmationService,
        OfflineService,
        Title,
        VendorJobClient,
        ConsumerJobClient,
        JobCacheService,
        MessageService,
        DialogService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CustomMsalInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeMsalFactory,
            deps: [MsalService],
            multi: true
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        TelemetryService,
        {
            provide: ErrorHandler,
            useClass: ApplicationinsightsAngularpluginErrorService
        },
        provideHttpClient(withInterceptorsFromDi()),
        providePrimeNG({
          theme: {
            preset: OpsViewerPreset,
            options: {
              darkModeSelector: '.opsviewer-dark'
            }
          }
        })
    ] })
export class AppModule { }
